/* @refresh reset */
import { palette } from "lib/miolo";
import noop from "lodash/fp/noop";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { createEditor } from "slate";
import type { ReactEditor, RenderLeafProps } from "slate-react";
import { Editable, Slate, withReact } from "slate-react";

interface IFinancialHighlightsContentProps {
  content: any;
}

export default function FinancialHighlightsContent({ content }: IFinancialHighlightsContentProps) {
  const editor = useMemo(() => withReact(createEditor() as ReactEditor), []);

  // const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
  const renderElement = useCallback((props: any) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);

  return (
    <Slate editor={editor} value={content} onChange={noop}>
      <Editable renderLeaf={renderLeaf} renderElement={renderElement} readOnly={true} />
    </Slate>
  );
}

export type IElementBlockType = "block-quote" | "bulleted-list" | "numbered-list" | "list-item" | string;

interface IElementProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>;
  children: ReactNode;
  element: {
    type: IElementBlockType;
  };
}

function Element({ attributes, children, element }: IElementProps) {
  switch (element.type) {
    case "block-quote":
      return (
        <blockquote
          {...attributes}
          style={{
            padding: "8px 12px",
            // TODO(andreyvital): replace with palette colors
            borderLeft: `solid 2px ${palette.blueBayoux}`,
          }}
        >
          {children}
        </blockquote>
      );

    case "bulleted-list":
      return (
        <ul
          {...attributes}
          className="list-disc"
          style={{
            margin: 0,
            paddingLeft: 24,
          }}
        >
          {children}
        </ul>
      );

    case "numbered-list":
      return (
        <ol
          {...attributes}
          className="list-decimal"
          style={{
            paddingLeft: 24,
          }}
        >
          {children}
        </ol>
      );

    case "list-item":
      return <li {...attributes}>{children}</li>;

    default:
      return <p {...attributes}>{children}</p>;
  }
}

interface ILeafProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>;
  children: ReactNode;
  leaf: {
    bold?: boolean;
    italic?: boolean;
  };
}

function Leaf({ attributes, children, leaf }: ILeafProps) {
  let leafChildren = children;

  if (leaf.bold) {
    leafChildren = <strong>{leafChildren}</strong>;
  }

  if (leaf.italic) {
    leafChildren = <em>{leafChildren}</em>;
  }

  return <span {...attributes}>{leafChildren}</span>;
}
